import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { Clock, Megaphone, Plus, Send, Trash2 } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fleet/ui";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@comandero/shared";
import { useI18n } from "@/providers/i18n-provider";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { useAdminCapabilities } from "../use-admin-capabilities";

type Campaign = components["schemas"]["EmailCampaign"];
type EmailTemplate = components["schemas"]["EmailTemplate"];

/** Newsletters/campaigns (campaign #11): create, schedule, and send to an audience. */
export function EmailCampaignManager() {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();
  /** Mirror of `Capability::ManageContent` (manage_content_middleware). */
  const canWrite = () => can("content.write");
  const [creating, setCreating] = createSignal(false);
  const [name, setName] = createSignal("");
  const [templateId, setTemplateId] = createSignal("");
  const [audience, setAudience] = createSignal("all");
  const [scheduledAt, setScheduledAt] = createSignal("");

  const campaignsQuery = createApiQuery<Campaign[], components["schemas"]["ErrorModel"]>(
    () => ["admin", "email", "campaigns"],
    async () => api.GET("/api/admin/email/campaigns"),
    () => ({ staleTime: 15_000 }),
  );
  const templatesQuery = createApiQuery<EmailTemplate[], components["schemas"]["ErrorModel"]>(
    () => ["admin", "email", "templates"],
    async () => api.GET("/api/admin/email/templates"),
    () => ({ staleTime: 30_000 }),
  );

  const createMutation = createApiMutation(
    async (vars: components["schemas"]["CreateCampaignDto"]) =>
      api.POST("/api/admin/email/campaigns", { body: vars }),
  );
  const scheduleMutation = createApiMutation(
    async (vars: components["schemas"]["CampaignActionDto"]) =>
      api.POST("/api/admin/email/campaigns/schedule", { body: vars }),
  );
  const sendNowMutation = createApiMutation(
    async (vars: components["schemas"]["CampaignActionDto"]) =>
      api.POST("/api/admin/email/campaigns/send-now", { body: vars }),
  );
  const deleteMutation = createApiMutation(
    async (vars: components["schemas"]["CampaignActionDto"]) =>
      api.POST("/api/admin/email/campaigns/delete", { body: vars }),
  );

  const handleCreate = async () => {
    try {
      await createMutation.mutateAsync({
        name: name(),
        templateId: templateId(),
        audience: audience(),
        context: {},
      });
      setCreating(false);
      setName("");
      await campaignsQuery.refetch();
    } catch (err) {
      console.error("create campaign error", err);
    }
  };

  const handleSendNow = async (id: string) => {
    await sendNowMutation.mutateAsync({ id });
    await campaignsQuery.refetch();
  };
  const handleSchedule = async (id: string) => {
    await scheduleMutation.mutateAsync({
      id,
      scheduledAt: scheduledAt() || new Date().toISOString(),
    });
    await campaignsQuery.refetch();
  };
  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync({ id });
    await campaignsQuery.refetch();
  };

  const inputClass =
    "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary";

  return (
    <div class="space-y-8">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Megaphone class="h-8 w-8 text-primary" />
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Campaigns`)}</h2>
        </div>
        {/* `read_only` sigue viendo la lista de campañas y su estado; lo que
            desaparece es lanzarlas. `send-now` dispara correo REAL a toda la
            base: ocultar, no deshabilitar. */}
        <Show when={canWrite()}>
          <Button class="gap-2" onClick={() => setCreating((v) => !v)}>
            <Plus class="h-4 w-4" />
            {t(msg`New Campaign`)}
          </Button>
        </Show>
      </div>

      <Show when={creating()}>
        <Card>
          <CardHeader>
            <CardTitle>{t(msg`New Campaign`)}</CardTitle>
          </CardHeader>
          <CardContent class="space-y-3">
            <label class="block text-sm">
              <span class="mb-1 block text-muted-foreground">{t(msg`Name`)}</span>
              <input
                class={inputClass}
                value={name()}
                onInput={(e) => setName(e.currentTarget.value)}
              />
            </label>
            <label class="block text-sm">
              <span class="mb-1 block text-muted-foreground">{t(msg`Template`)}</span>
              <select
                class={inputClass}
                value={templateId()}
                onChange={(e) => setTemplateId(e.currentTarget.value)}
              >
                <option value="">{t(msg`Select a template`)}</option>
                <For each={templatesQuery.data ?? []}>
                  {(tpl) => <option value={tpl.id}>{tpl.name}</option>}
                </For>
              </select>
            </label>
            <label class="block text-sm">
              <span class="mb-1 block text-muted-foreground">{t(msg`Audience`)}</span>
              <select
                class={inputClass}
                value={audience()}
                onChange={(e) => setAudience(e.currentTarget.value)}
              >
                <option value="all">{t(msg`All users`)}</option>
                <option value="verified">{t(msg`Verified users`)}</option>
                <option value="admins">{t(msg`Admins`)}</option>
              </select>
            </label>
            <Button
              size="sm"
              disabled={!name() || !templateId() || createMutation.isPending}
              onClick={handleCreate}
            >
              {t(msg`Create`)}
            </Button>
          </CardContent>
        </Card>
      </Show>

      {/* P23/I3: «no hay respuesta» != «no hay nada». El bloque de
          contenido se ENVUELVE (no se encadena): en error `isLoading` es
          `false`, asi que un `when={!q.isLoading && !q.isError}` con
          `fallback={esqueleto}` pintaria error Y esqueleto a la vez. */}
      <Show when={campaignsQuery.isError}>
        <QueryErrorState
          testId="campaigns-error"
          compact
          onRetry={() => void campaignsQuery.refetch()}
        />
      </Show>
      <Show when={!campaignsQuery.isError}>
        <Show
          when={!campaignsQuery.isLoading}
          fallback={<Skeleton class="h-24 w-full rounded-xl" />}
        >
          <div class="space-y-3">
            <For each={campaignsQuery.data ?? []}>
              {(c) => (
                <div class="flex items-center justify-between rounded-xl border border-border bg-card p-4">
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <span class="truncate font-semibold">{c.name}</span>
                      <Badge variant="secondary">{c.audience}</Badge>
                      <Badge>{c.status}</Badge>
                    </div>
                  </div>
                  <div class="flex shrink-0 items-center gap-2">
                    <Show when={canWrite()}>
                      <input
                        type="datetime-local"
                        class="rounded-lg border border-border bg-background px-2 py-1 text-xs"
                        onInput={(e) =>
                          setScheduledAt(new Date(e.currentTarget.value).toISOString())
                        }
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        class="gap-1"
                        onClick={() => handleSchedule(c.id)}
                      >
                        <Clock class="h-4 w-4" />
                        {t(msg`Schedule`)}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        class="gap-1"
                        onClick={() => handleSendNow(c.id)}
                      >
                        <Send class="h-4 w-4" />
                        {t(msg`Send now`)}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        class="gap-1 text-destructive"
                        onClick={() => handleDelete(c.id)}
                      >
                        <Trash2 class="h-4 w-4" />
                      </Button>
                    </Show>
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
}
